// ScatterChart.js
import React, { useEffect, useState } from "react";
import { Scatter } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";

// Register Chart.js components for scatter chart
ChartJS.register(PointElement, LinearScale, CategoryScale, Tooltip, Legend);

// Define a general structure for data
const dataStructure = {
  value: 0, // General value (could be used for size or other purposes)
  x: 0, // X-axis value
  y: 0, // Y-axis value
  legend: "", // Legend label (replaces Title)
};

// Function to generate random RGBA color
function getRandomColor(opacity = 0.8) {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export default function ScatterChart({
  data,
  xLabel = "X Axis",
  yLabel = "Y Axis",
}) {
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [validatedData, setValidatedData] = useState([]);
  const [selectedLegend, setSelectedLegend] = useState(null); // Track selected legend item

  useEffect(() => {
    // Validate data structure using the general dataStructure
    const newValidatedData = data.map((item) => ({
      ...dataStructure,
      ...item,
    }));

    // Generate a color map based on legend values
    const legendColorMap = {};
    newValidatedData.forEach((item) => {
      if (!legendColorMap[item.legend]) {
        legendColorMap[item.legend] = getRandomColor();
      }
    });

    // Prepare initial scatter chart data
    const scatterData = newValidatedData.map((item) => ({
      x: item.x,
      y: item.y,
    }));

    const backgroundColors = newValidatedData.map(
      (item) => legendColorMap[item.legend]
    );

    // Prepare the chart data
    const formattedChartData = {
      datasets: [
        {
          label: "Data Points",
          data: scatterData,
          backgroundColor: backgroundColors,
          pointRadius: 6,
          pointHoverRadius: 9,
          borderWidth: 1,
          borderColor: "#fff",
        },
      ],
    };

    setValidatedData(newValidatedData);
    setChartData(formattedChartData);
    setIsLoading(false);
  }, [data]);

  // Function to update chart data based on selected legend
  const updateChartData = (selectedItem) => {
    if (!selectedItem) {
      // Reset to show all data
      const scatterData = validatedData.map((item) => ({
        x: item.x,
        y: item.y,
      }));
      const backgroundColors = validatedData.map(
        (item) => getRandomColor() // Re-generate colors or use a stored map if needed
      );
      setChartData({
        datasets: [
          {
            ...chartData.datasets[0],
            data: scatterData,
            backgroundColor: backgroundColors,
          },
        ],
      });
      setSelectedLegend(null);
    } else {
      // Filter data to show only the selected legend item
      const filteredData = validatedData.filter(
        (item) => item.legend === selectedItem.text
      );
      const scatterData = filteredData.map((item) => ({
        x: item.x,
        y: item.y,
      }));
      const backgroundColors = filteredData.map(
        () => selectedItem.fillStyle // Use the selected item's color
      );
      setChartData({
        datasets: [
          {
            ...chartData.datasets[0],
            data: scatterData,
            backgroundColor: backgroundColors,
          },
        ],
      });
      setSelectedLegend(selectedItem.text);
    }
  };

  // Chart options with improved legend
  // Chart options with improved legend
  // Chart options with improved legend
  const options = {
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: xLabel,
          font: {
            size: 14,
          },
        },
        beginAtZero: true,
      },
      y: {
        title: {
          display: true,
          text: yLabel,
          font: {
            size: 14,
          },
        },
        beginAtZero: true,
      },
    },
    plugins: {
      datalabels: {
        display: false, // Disable data labels
      },
      legend: {
        display: true,
        position: "bottom", // Place the legend under the graph
        align: "start", // Align legends to the left
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 8, // Adjust padding between legend items
          font: {
            size: 12,
            family: "Arial",
            weight: "normal",
          },
          color: "#333",
          usePointStyle: true,
          generateLabels: (chart) => {
            const { data } = chart;
            const uniqueLegends = [
              ...new Set(validatedData.map((item) => item.legend)),
            ];
            return uniqueLegends.map((legendText, index) => {
              const firstItemIndex = validatedData.findIndex(
                (item) => item.legend === legendText
              );
              return {
                text: legendText,
                fillStyle: data.datasets[0].backgroundColor[firstItemIndex],
                strokeStyle: "#fff",
                lineWidth: 1,
                pointStyle: "circle",
                hidden: selectedLegend && selectedLegend !== legendText, // Hide unselected
                index: firstItemIndex, // Store index for reference
              };
            });
          },
        },
        onClick: (e, legendItem, legend) => {
          // Custom onClick behavior
          if (selectedLegend === legendItem.text) {
            // If clicking the already selected item, reset to show all
            updateChartData(null);
          } else {
            // Show only the selected legend item
            updateChartData(legendItem);
          }
        },
        onHover: (event) => {
          event.native.target.style.cursor = "pointer";
        },
        onLeave: (event) => {
          event.native.target.style.cursor = "default";
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const item = validatedData.find(
              (d, i) =>
                d.x === context.dataset.data[index].x &&
                d.y === context.dataset.data[index].y
            );
            return `${item.legend}: Impact=${item.x}, Probability=${item.y}`;
          },
        },
      },
    },
  };

  const chartHeight = 400;

  return (
    <div style={{ height: `${chartHeight}px`, width: "100%" }}>
      {isLoading ? (
        <p>Loading chart...</p>
      ) : chartData ? (
        <Scatter data={chartData} options={options} />
      ) : (
        <p>No chart data available.</p>
      )}
    </div>
  );
}
